import { ITradeRequest } from 'models/trade/i-trade-request.interface';

import { OfferTypesEnum } from 'enums/offer-types.enum';

import { OFFER_TYPES, TRADE_PARAMS } from 'constants/market.constant';
import { NETWORK_SETTINGS } from 'constants/network.constant';

interface IDomain {
  name: string;
  version: string;
  chainId: number;
  verifyingContract: string;
}

/**
 * Service for handling Web3 signing operations
 * Manages message signing for various blockchain operations
 */
export class SigningService {
  private readonly settings = NETWORK_SETTINGS;

  constructor(private ethereum: any) {}

  /**
   * Creates an EIP712 domain object for signing
   */
  private async createDomain(getChainId: () => Promise<number>): Promise<IDomain> {
    return {
      chainId: await getChainId(),
      name: this.settings.TRADE.NAME,
      version: this.settings.TRADE.VERSION,
      verifyingContract: this.settings.TRADE.ADDRESS,
    };
  }

  /**
   * Creates a personal signature for a message
   */
  async createSignature(toSignature: string, walletAddress: string): Promise<string> {
    const message = `0x${Buffer.from(toSignature, 'utf8').toString('hex')}`;
    const signature = await this.ethereum.request({
      method: 'personal_sign',
      params: [message, walletAddress],
    });

    return signature;
  }

  /**
   * Signs a trade offer using EIP712 typed data
   */
  async signTradeOffer(walletAddress: string, message: any, getChainId: () => Promise<number>): Promise<string> {
    const domain = await this.createDomain(getChainId);

    return this.ethereum.request({
      method: TRADE_PARAMS.signMethod,
      params: [
        walletAddress,
        JSON.stringify({
          domain,
          message,
          types: {
            [OfferTypesEnum.EIP712Domain]: OFFER_TYPES[OfferTypesEnum.EIP712Domain],
            [OfferTypesEnum.TradeItem]: OFFER_TYPES[OfferTypesEnum.TradeItem],
            [OfferTypesEnum.TradeOffer]: OFFER_TYPES[OfferTypesEnum.TradeOffer],
          },
          primaryType: OfferTypesEnum.TradeOffer,
        }),
      ],
    });
  }

  /**
   * Signs a market bid offer using EIP712 typed data
   */
  async signMarketBid(
    tradeRequest: ITradeRequest,
    accountAddress: string,
    getChainId: () => Promise<number>
  ): Promise<string> {
    const domain = await this.createDomain(getChainId);

    return this.ethereum.request({
      method: TRADE_PARAMS.signMethod,
      params: [
        accountAddress,
        JSON.stringify({
          domain,
          message: tradeRequest,
          types: {
            [OfferTypesEnum.EIP712Domain]: OFFER_TYPES[OfferTypesEnum.EIP712Domain],
            [OfferTypesEnum.TradeItem]: OFFER_TYPES[OfferTypesEnum.TradeItem],
            [OfferTypesEnum.TradeBid]: OFFER_TYPES[OfferTypesEnum.TradeBid],
          },
          primaryType: OfferTypesEnum.TradeBid,
        }),
      ],
    });
  }
}
